import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { ContactRepository } from '../../states/contact.repository';
import { ContactModel } from '../../models/contact.model';
import { formatVeterinarianName } from '../../../animals/models/medical/medical.helpers';

interface VeterinarianOption {
  value: number;
  label: string;
  hint: string;
}

/**
 * Auswahl eines Tierarztes (Kontakt mit `isVeterinary`) samt Schnellanlage über „+“.
 * Wert ist die Contact-Id.
 */
@Component({
  selector: 'app-veterinarian-select',
  templateUrl: './veterinarian-select.component.html',
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VeterinarianSelectComponent), multi: true }]
})
export class VeterinarianSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() inputId = 'veterinarian';
  @Input() placeholder: string | null = null;
  @Input() invalid = false;
  /** Kontakt-Ids, die nicht angeboten werden (z. B. bereits verknüpfte Haustierärzte). */
  @Input() excludeIds: number[] = [];
  @Output() created = new EventEmitter<ContactModel>();

  options: VeterinarianOption[] = [];
  value: number | null = null;
  disabled = false;

  createVisible = false;
  createValid = false;
  saving = false;
  newContact: ContactModel = {} as ContactModel;

  private _veterinarians: ContactModel[] = [];
  private _contactsById: Record<number, ContactModel> = {};
  private readonly _destroy$ = new Subject<void>();
  private _onChange: (value: number | null) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor(
    private readonly _contactRepository: ContactRepository,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._contactRepository.loadAllIfNeeded();
    combineLatest([this._contactRepository.selectVeterinarians$(), this._contactRepository.selectById$()])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([veterinarians, byId]) => {
        this._veterinarians = veterinarians;
        this._contactsById = byId;
        this.buildOptions();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['excludeIds'] && !changes['excludeIds'].firstChange) {
      this.buildOptions();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  writeValue(value: number | null): void {
    this.value = value ?? null;
    this.buildOptions();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  onSelect(value: number | null): void {
    this.value = value ?? null;
    this._onChange(this.value);
    this._onTouched();
  }

  markTouched(): void {
    this._onTouched();
  }

  openCreate(): void {
    this.newContact = { isVeterinary: true } as ContactModel;
    this.createValid = false;
    this.createVisible = true;
  }

  closeCreate(): void {
    this.createVisible = false;
    this.newContact = {} as ContactModel;
  }

  saveCreate(): void {
    if (!this.createValid || this.saving) return;
    this.saving = true;
    this._contactRepository.create({ ...this.newContact, isVeterinary: true })
      .pipe(take(1))
      .subscribe({
        next: (contact: ContactModel | null) => {
          this.saving = false;
          if (!contact?.id) return;
          this.createVisible = false;
          this.newContact = {} as ContactModel;
          this.onSelect(contact.id);
          this.created.emit(contact);
          this._cdr.markForCheck();
        },
        error: () => {
          this.saving = false;
          this._cdr.markForCheck();
        }
      });
  }

  private buildOptions(): void {
    const exclude = new Set(this.excludeIds.filter(id => id !== this.value));
    const contacts = this._veterinarians.filter(contact => !exclude.has(contact.id));

    // Ein bereits gesetzter Kontakt bleibt wählbar, auch wenn er (noch) nicht als Tierarzt markiert ist.
    if (this.value !== null && !contacts.some(contact => contact.id === this.value) && this._contactsById[this.value]) {
      contacts.push(this._contactsById[this.value]);
    }

    this.options = contacts
      .map(contact => ({
        value: contact.id,
        label: formatVeterinarianName(contact) || `#${contact.id}`,
        hint: this.hintFor(contact),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    this._cdr.markForCheck();
  }

  private hintFor(contact: ContactModel): string {
    const person = contact.companyName
      ? [contact.title, contact.firstName, contact.lastName].filter(Boolean).join(' ')
      : '';
    return [person, [contact.zipCode, contact.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
  }
}
